import { inject, injectable } from 'inversify';
import { reaction } from 'mobx';
import { ViewPortManager } from '@/components/viewPort/viewPortManager';
import { TYPES } from '@/engine/ioc';
import { TYPES as IndicationsTYPES } from './modules/indications/iocTypes';
import {
  ISelectableItem,
  ViewPortType,
  type ICenterLineViewModel,
  type IImageSliceViewModel,
  type IMeasurementViewModel,
  type IObjectViewModel,
  type IViewPortController,
  type IViewPortManager,
  type IViewPortManagerBuilder
} from './interfaces';
import { type ICineloopInfo, ViewPortKind } from './interfaces';
import type { ViewEntity, ViewPortInfo } from './models/viewEntity/view.entity.ts';
import type { ToolsManagerFactory } from './modules/indications/ToolsManager.factory';
import type { ViewsService } from './Views.service';
import type { ToolsManager } from './modules/indications/ToolsManager';
import { ViewItemsVisibilityManager } from './ViewItemsVisibilityManager';
import { ViewItemsSelectionManager } from './ViewItemsSelectionManager';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { ImagesService } from './modules/images/Images.service';
import { TYPES as ImagesTYPES } from './modules/images/iocTypes';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { SelectionReactionController } from './SelectionReaction.controller';
import { CenterlineSelectionManager } from './CenterlineSelectionManager';
import { PhaseViewItemsController } from './PhaseViewItems.controller';

import type { CasePhaseData } from './CasePhaseData/CasePhaseData.ts';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';
import type { ViewPortTooltipViewModel } from './models/ViewPortTooltipViewModel';
import type { SurgeryRegistry } from './registries/SurgeryRegistry.ts';

import { CORE_TYPES } from '@/engine/app/modules/core';
import { ViewLayoutConfigurationProvider, ViewLayouts } from '@/components/layout/ViewLayout.tsx';

@injectable()
export class ViewController implements ViewLayoutConfigurationProvider {
  private readonly _viewsService: ViewsService;

  private readonly _imagesService: ImagesService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _casePhaseData: CasePhaseData;

  private readonly _viewPortManagerBuilder: IViewPortManagerBuilder;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _cineloopInfo: ICineloopInfo;

  private readonly _surgeryRegistry: SurgeryRegistry;

  private _viewItemsVisibilityManager?: IDisposable;

  private _viewItemsSelectionManager?: IDisposable;

  private _selectionOnImagesSyncController?: IDisposable;

  private _centerlineSelectionManager?: CenterlineSelectionManager;

  private _phaseViewItemsController?: PhaseViewItemsController;

  private _viewsLayout?: ViewLayouts;

  private _viewPortControllers: Map<string, IViewPortController> = new Map();

  private _viewPortManagers: Map<string, IViewPortManager> = new Map();

  private _toolsManagerFactory: ToolsManagerFactory;

  private _toolsManager?: ToolsManager;

  public constructor(
    @inject(TYPES.IViewPortManagerBuilder)
    viewPortManagerBuilder: IViewPortManagerBuilder,
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(IndicationsTYPES.ToolsManagerFactory)
    toolsManagerFactory: ToolsManagerFactory,
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(TYPES.CasePhaseData) casePhaseData: CasePhaseData,
    @inject(TYPES.ICineloopInfo) cineloopInfo: ICineloopInfo,
    @inject(CORE_TYPES.ObservabilityLoggerService) loggerService: ObservabilityLoggerService,
    @inject(TYPES.SurgeryRegistry) surgeryRegistry: SurgeryRegistry
  ) {
    this._viewsService = viewsService;
    this._imagesService = imagesService;
    this._casePhaseData = casePhaseData;
    this._planningAlgorithm = planningAlgorithm;
    this._viewPortManagerBuilder = viewPortManagerBuilder;
    this._viewsLayout = this.getLayout();
    this._toolsManagerFactory = toolsManagerFactory;
    this._cineloopInfo = cineloopInfo;
    this._loggerService = loggerService;
    this._surgeryRegistry = surgeryRegistry;

    reaction(
      () => this._viewsService.currentView,
      (view) => this._onCurrentViewChanged(view)
    );

    // [chebotarev] the reaction below performs objects adding needed for measurements indication
    // in fact this code causes objects to be added to ViewPort twice!
    // TODO: find out why how to get rid of this double objects adding
    reaction(
      () => this._viewsService.currentView?.getMeasurements().slice(),
      (viewModels) => {
        if (viewModels) {
          Array.from(this._viewPortManagers.values()).forEach((vpm) => {
            this._setMeasurementsToViewPort(vpm, viewModels);
          });
        }
      }
    );

    reaction(
      () => this._viewsService.currentView?.getObjects().slice(),
      (viewModels) => {
        if (viewModels) {
          Array.from(this._viewPortManagers.values()).forEach((vpm) => {
            this._setObjectsToViewPort(vpm, viewModels);
          });
        }
      }
    );

    reaction(
      () => this._cineloopInfo.isCineloopActive,
      (isActive) => {
        this._viewPortManagers.forEach((vpm) => vpm.setPickingPossibility(!isActive));
      }
    );

    reaction(
      () => this._viewsService.currentView?.getVisibilityDependencies().slice(),
      (dependencies) => {
        this._viewItemsVisibilityManager?.dispose();
        if (dependencies !== undefined) {
          this._viewItemsVisibilityManager = new ViewItemsVisibilityManager(this._viewsService, dependencies);
        }
      }
    );

    reaction(
      () => {
        const clippingPlanes = this._viewsService.currentView?.clippingPlanes;
        return clippingPlanes ? Array.from(clippingPlanes.values()).slice() : undefined;
      },
      (clippingPlanes) => {
        if (clippingPlanes !== undefined) {
          this.getViewPortController(ViewPortType.ThreeD)?.setClippingPlanes(clippingPlanes);
        }
      }
    );
  }

  public resetView(viewPortKind?: ViewPortKind): void {
    this._initCameras(viewPortKind);
  }

  private _onCurrentViewChanged(view: ViewEntity | null): void {
    this._toolsManager?.dispose();
    this._viewItemsVisibilityManager?.dispose();
    this._viewItemsSelectionManager?.dispose();
    this._centerlineSelectionManager?.dispose();
    this._selectionOnImagesSyncController?.dispose();
    this._phaseViewItemsController?.dispose();
    this._viewPortManagers.forEach((vpm) => vpm.dispose());
    this._viewPortManagers.clear();
    //this._viewPortControllers.forEach((vpc) => vpc.dispose());
    //this._viewPortControllers.clear();
    if (view && this._viewsLayout == this.getLayout()) {
      this._loadImages(view.getImageStackDescriptor());
      this._setViewPortControllers(view);
      this._initScenes();
      this._initCameras();
    }
    this._viewsLayout = this.getLayout();
  }

  private _setViewPortControllers(view: ViewEntity, viewPortDescriptorList?: Array<string>): void {
    this._viewPortControllers.forEach((viewPortController, viewPortDescriptor) => {
      if (viewPortDescriptorList && !viewPortDescriptorList.includes(viewPortDescriptor)) {
        return;
      }
      viewPortController.setCursor('');

      const viewPort = this._getViewPortInfo(view, viewPortDescriptor);
      const viewPortManagerStrategy = this._viewPortManagerBuilder.buildStrategy(
        this,
        this._viewsService,
        viewPort,
        viewPortController
      );
      viewPortController.setStrategy(viewPortManagerStrategy.getViewPortControllerStrategy());
      const viewPortManager = new ViewPortManager(
        viewPortManagerStrategy,
        viewPortDescriptor,
        viewPortController,
        this._loggerService,
        this._surgeryRegistry.getPickablesSelector()
      );
      this._viewPortManagers.set(viewPortDescriptor, viewPortManager);
      this._setMeasurementsToViewPort(viewPortManager, view.getMeasurements());
      this._setObjectsToViewPort(viewPortManager, view.getObjects());
      this._setCenterlinesToViewPort(viewPortManager, view.getCenterLines());
      this._setTooltipsToViewPort(viewPortManager, view.getTooltipsForViewPort(viewPortDescriptor));
      viewPortManager.createImageSlices(view.getImageSlices());
    });
  }

  private _loadImages(descriptor: string): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._imagesService.loadImagesAsync(descriptor);
  }

  private _getViewPortInfo(view: ViewEntity, viewPortDescriptor: string): ViewPortInfo {
    const viewPort = view.getViewPorts().find((vp) => vp.descriptor == viewPortDescriptor);
    if (!viewPort) {
      throw new Error('Discrepancy between View Port and its Manager');
    }
    return viewPort;
  }

  public registerViewPort(viewPortDescriptor: string, viewPortController: IViewPortController): void {
    this.unregisterViewPort(viewPortDescriptor);
    this._viewPortControllers.set(viewPortDescriptor, viewPortController);
    if (this._viewPortControllers.size == this._viewsService.currentView?.getViewPorts().length) {
      // TODO: [chebotarev] Not the right place for initialization
      this._onCurrentViewChanged(this._viewsService.currentView);
    }
  }

  public unregisterViewPort(viewPortDescriptor: string): void {
    this._viewPortManagers.get(viewPortDescriptor)?.dispose();
    this._viewPortManagers.delete(viewPortDescriptor);
    this._viewPortControllers.get(viewPortDescriptor)?.dispose();
    this._viewPortControllers.delete(viewPortDescriptor);
  }

  public getViewPortController(viewPortDescriptor: string): IViewPortController | undefined {
    return this._viewPortControllers.get(viewPortDescriptor);
  }

  public getViewPortManager(viewPortDescriptor: string): IViewPortManager | undefined {
    return this._viewPortManagers.get(viewPortDescriptor);
  }

  public getRegisteredViewPorts(kind?: ViewPortKind): Array<IViewPortManager> {
    const ret = Array.from(this._viewPortManagers.values());
    if (kind) {
      const currentView = this._viewsService.currentView;
      if (!currentView) {
        throw new Error('currentView must be existent so far');
      }
      return ret.filter((vpm) => this._getViewPortInfo(currentView, vpm.getDescriptor()).kind == kind);
    } else {
      return ret;
    }
  }

  public getViewPorts(): Array<ViewPortInfo> {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('currentView must be existent so far');
    }
    return currentView.getViewPorts();
  }

  public getImageSlices(): Array<IImageSliceViewModel> {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      throw new Error('currentView must be existent so far');
    }
    return currentView.getImageSlices();
  }

  public getLayout(): ViewLayouts {
    // naive implementation
    const viewPortsCount = this.getViewPorts().length;
    return viewPortsCount == 4 ? ViewLayouts.QUADRO : viewPortsCount == 2 ? ViewLayouts.DOUBLE : ViewLayouts.SINGLE;
  }

  private _setObjectsToViewPort(vpm: IViewPortManager, viewModels: Array<IObjectViewModel>): void {
    vpm.setObjects(viewModels);
  }

  private _setCenterlinesToViewPort(vpm: IViewPortManager, viewModels: Array<ICenterLineViewModel>): void {
    vpm.setCenterLines(viewModels);
  }

  private _setMeasurementsToViewPort(vpm: IViewPortManager, viewModels: Array<IMeasurementViewModel>): void {
    vpm.setMeasurements(viewModels);
  }

  private _setTooltipsToViewPort(vpm: IViewPortManager, viewModels: Array<ViewPortTooltipViewModel>): void {
    vpm.setTooltips(viewModels);
  }

  private _initCameras(viewPortKind?: ViewPortKind): void {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      return;
    }

    if (viewPortKind !== ViewPortKind.ThreeD) {
      const imagesRoiConfig = currentView.getImagesRoiConfig();
      if (imagesRoiConfig) {
        const imagesRoi = this._planningAlgorithm.getValueStrict(imagesRoiConfig.descriptor, DT.RegionOfInterest).value;
        this._imagesService.syncSliceWithPlane(
          imagesRoiConfig.syncSliceDescriptor,
          imagesRoi.ptO,
          imagesRoi.dirZ,
          imagesRoi.dirY
        );
      } else {
        if (currentView.getActiveCenterLineDescriptor()) {
          currentView.getSelectableItems().forEach((vm: ISelectableItem) => {
            vm.setSelection(false);
          });
          this._centerlineSelectionManager?.setActiveCenterLineDescriptor(undefined);
        }
        this._imagesService.resetMpr(false, true);
      }
      this.getRegisteredViewPorts(ViewPortKind.ImageSlice).forEach((vpm) => {
        vpm.initCamera();
      });
    }

    if (viewPortKind !== ViewPortKind.ImageSlice) {
      this.getRegisteredViewPorts(ViewPortKind.ThreeD).forEach((vpm) => {
        vpm.initCamera();
      });
    }
  }

  private _initScenes(): void {
    const currentView = this._viewsService.currentView;
    if (!currentView) {
      return;
    }

    for (const sliceInfo of currentView.getImageSlices()) {
      this._imagesService.setSlicerMode(sliceInfo.descriptor, sliceInfo.imageSlicerMode);
    }

    this._toolsManager = this._toolsManagerFactory.createToolsManager(Array.from(this._viewPortManagers.values()));

    this._viewItemsVisibilityManager = new ViewItemsVisibilityManager(
      this._viewsService,
      currentView.getVisibilityDependencies()
    );

    this._viewItemsSelectionManager = new ViewItemsSelectionManager(
      this._viewsService,
      currentView.getSelectionDependencies()
    );

    this._centerlineSelectionManager = new CenterlineSelectionManager(
      this._imagesService,
      this._planningAlgorithm,
      this._viewsService
    );

    this._selectionOnImagesSyncController = new SelectionReactionController(
      this._viewsService,
      this,
      this._imagesService,
      this._planningAlgorithm
    );

    this._phaseViewItemsController = new PhaseViewItemsController(this._viewsService, this._casePhaseData);
  }

  public setActiveCenterLineDescriptor(descriptor: string | undefined): void {
    if (!this._centerlineSelectionManager) {
      throw new Error('Centerline Selection Manager is undefined');
    }
    this._centerlineSelectionManager.setActiveCenterLineDescriptor(descriptor);
  }
}
